import {useCallback, useMemo, useState} from 'react';
import {Button, Table} from '@flixbus/honeycomb-react';
import {Icon, IconEditSolid} from '@flixbus/honeycomb-icons-react';
import VehicleCostsPanel from '@/components/VehicleCosts/VehicleCostsPanel/VehicleCostsPanel';
import {
  PartnerCostVehicleCostsPanelData,
  RegionalCostVehicleCostsPanelData,
  VehicleCostsPanelData
} from '@/components/VehicleCosts/VehicleCostsPanel/VehicleCostsPanel.types';
import {ValueDimension} from '@/Pages/Countries/components/index.constants';
import styles from './VehicleCostsTable.module.scss';
import {VehicleCostTableProps} from './VehicleCostsTable.types';
import {getVehicleCostTableRowConfig} from './VehicleCostsTable.utils';

const VehicleCostTable: React.FC<VehicleCostTableProps> = ({
  country,
  year,
  config,
  vehicleCost,
  fallbackConfig,
  fallbackVehicleCost,
  updateFn
}) => {
  const isRepairCostPerMonth = useMemo(
    () => !!config.find(({key}) => key === 'repairCostPerMonth'),
    [config]
  );

  const rows = useMemo(() => {
    return config.map(rowConfig => {
      const {title, originalValue, fallbackValue} =
        getVehicleCostTableRowConfig(
          rowConfig,
          vehicleCost,
          fallbackConfig,
          fallbackVehicleCost
        );

      const titleCell = (
        <div className={styles.cellContainer}>
          {title}
          {fallbackValue !== null && (
            <div className={styles.fallback}>
              Regional value = {fallbackValue}
            </div>
          )}
        </div>
      );

      return [titleCell, originalValue];
    });
  }, [config, fallbackConfig, fallbackVehicleCost, vehicleCost]);

  const [panelData, setPanelData] = useState<VehicleCostsPanelData | null>(
    null
  );

  const handleEditClick = useCallback(() => {
    const baseData = {
      adblueCost: vehicleCost.adblueCost,
      cleaningCost: vehicleCost.cleaningCost,
      dieselCost: vehicleCost.dieselCost,
      legalCost: vehicleCost.legalCost,
      lngCost: vehicleCost.lngCost,
      otherCost: vehicleCost.otherCost,
      overheadCost: vehicleCost.overheadCost,
      repairCostUnit: isRepairCostPerMonth
        ? ValueDimension.PER_MONTH
        : ValueDimension.PER_DISTANCE_UNIT,
      repairCostValue: isRepairCostPerMonth
        ? vehicleCost.repairCostPerMonth
        : vehicleCost.repairCostPerDistanceUnit,
      tiresCost: vehicleCost.tiresCost
    };

    const isRegionalVehicleCost = 'flixStandardCost' in vehicleCost;
    const panelData = isRegionalVehicleCost
      ? ({
          ...baseData,
          flixStandardCost: vehicleCost.flixStandardCost
        } as RegionalCostVehicleCostsPanelData)
      : (baseData as PartnerCostVehicleCostsPanelData);

    setPanelData(panelData);
  }, [isRepairCostPerMonth, vehicleCost]);

  const handleClose = useCallback(() => {
    setPanelData(null);
  }, []);

  return (
    <>
      <div className={styles.title}>
        <h3 className={styles.leftSide}>
          Define vehicle cost for {country.name}
        </h3>
        <Button onClick={handleEditClick}>
          <Icon InlineIcon={IconEditSolid} aria-hidden="true" />
          Edit cost
        </Button>
      </div>

      <Table rows={rows} extraClasses="vehicle-cost-table" />

      <VehicleCostsPanel
        country={country}
        year={year}
        panelData={panelData}
        config={config}
        updateFn={updateFn}
        onClose={handleClose}
      />
    </>
  );
};

export default VehicleCostTable;
